'use client';

import { UIEvent, useState } from 'react';
import { faMinus, faPlus, faTimes } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { ScheduledDayColumn } from './_components/scheduled-day-column.component';
import { ScheduledDaysHeader } from './_components/scheduled-days-header.component';

import { Button } from '@/components/ui/button/button.component';
import { EntityAvatar } from '@/components/ui/avatar/entity-avatar';
import { Icon } from '@/components/ui/icon.component';
import { Sheet, SheetClose, SheetContent } from '@/components/ui/sheet';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { SlotsSkeleton } from '@/app/(main)/(pages)/_components/schedules/slots-skeleton.component';
import { dropPastScheduledSlots } from '@/app/(main)/(pages)/_components/schedules/_utils/drop-past-scheduled-slots';
import { useScheduleSlots } from '@/app/(main)/(pages)/_components/schedules/_hooks/use-schedule-slots.hook';
import { SchedulesSearchSlot, SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { useIsGreaterThan } from '@/hooks/use-is-greather-than.hook';
import { useSlotFilters } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-slot-filters.hook';
import { cn } from '@/utils/cn-tw-merge';
import { partnerBranchDisplayName } from '@/utils/formatters/branch';
import { professionalDisplayName } from '@/utils/formatters/professional-name';

const MODAL_DAYS_NARROW = 3;
const MODAL_DAYS_WIDE = 4;
const MODAL_SLOTS_PER_COLUMN = 10;

interface SearchCardClinicScheduledModalProps {
  scheduleId: string | undefined;
  schedule: SearchPartnerData;
  now: Date;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectSlot: (slot: SchedulesSearchSlot) => void;
}

export function SearchCardClinicScheduledModal({
  scheduleId,
  schedule,
  now,
  open,
  onOpenChange,
  onSelectSlot,
}: SearchCardClinicScheduledModalProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="center-v2"
        className="md:h-fit md:w-[90vw] md:rounded-2xl md:max-h-[67vh] md:max-w-[580px] lg:max-w-[600px] xl:max-w-[640px] 3xl:max-w-[680px] p-0"
        hasHeader
        data-testid={DataTestIdEnum.SEARCH_CARD_CLINIC_SCHEDULED_MODAL}
      >
        <ScheduledModalContent
          scheduleId={scheduleId}
          schedule={schedule}
          now={now}
          onOpenChange={onOpenChange}
          onSelectSlot={onSelectSlot}
        />
      </SheetContent>
    </Sheet>
  );
}

interface ScheduledModalContentProps {
  scheduleId: string | undefined;
  schedule: SearchPartnerData;
  now: Date;
  onOpenChange: (open: boolean) => void;
  onSelectSlot: (slot: SchedulesSearchSlot) => void;
}

function ScheduledModalContent({
  scheduleId,
  schedule,
  now,
  onOpenChange,
  onSelectSlot,
}: ScheduledModalContentProps) {
  const isWideScreen = useIsGreaterThan('sm');
  const limitDays = isWideScreen ? MODAL_DAYS_WIDE : MODAL_DAYS_NARROW;

  const slotFilters = useSlotFilters();

  const { isLoading, days: fetchedDays, hasMore, offset, goNext, goPrev } = useScheduleSlots({
    scheduleId,
    limitDays,
    filters: slotFilters,
  });
  const days = dropPastScheduledSlots(fetchedDays, now);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const { professional } = schedule;
  const entityName = professional?.id
    ? professionalDisplayName(professional)
    : partnerBranchDisplayName(schedule.partner);
  const entityAvatar = professional?.id ? professional.avatar : schedule.partner.avatar;
  const entityType = professional?.id ? EntityTypeEnum.PROFESSIONAL : EntityTypeEnum.BRANCH;

  const maxSlotsInAnyDay = days.reduce((max, day) => Math.max(max, day.slots.length), 0);
  const canExpand = maxSlotsInAnyDay > MODAL_SLOTS_PER_COLUMN;
  const visibleSlotsLimit = isExpanded ? maxSlotsInAnyDay : MODAL_SLOTS_PER_COLUMN;
  const hasDays = !isLoading && days.length > 0;
  const canNavigateDays = !isLoading && (offset > 0 || hasMore);

  const toggleExpand = () => setIsExpanded((current) => !current);
  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    setIsScrolled(event.currentTarget.scrollTop > 0);
  };

  return (
    <>
      <div
        className={cn(
          'relative flex flex-col w-full shrink-0',
          isScrolled && 'border-b border-gray-300 shadow-modal',
        )}
      >
        <div className="flex items-start gap-2 pt-5 pl-6 pr-5 pb-4 w-full">
          <Typography variant={TypographyVariant.MODAL_TITLE} className="flex-1">
            Horários disponíveis
          </Typography>
          <SheetClose
            asChild
            className="text-gray-500 cursor-pointer self-start hover:text-gray-900"
            data-testid={DataTestIdEnum.SEARCH_CARD_CLINIC_SCHEDULED_MODAL_CLOSE}
            onClick={() => onOpenChange(false)}
          >
            <button type="button" aria-label="Fechar" className="size-8 flex items-center justify-center">
              <Icon icon={faTimes} size="xl" />
            </button>
          </SheetClose>
        </div>

        <div className={cn('flex flex-col gap-5 px-6 pt-4 w-full', hasDays ? 'pb-2' : 'pb-5')}>
          <div className="flex items-start gap-4 w-full">
            <EntityAvatar
              alt={`Foto de perfil de ${entityName}`}
              size="lg"
              src={entityAvatar}
              type={entityType}
              className="rounded-lg"
            />
            <Typography
              variant={TypographyVariant.MEDIUM_SUBTITLE}
              className="flex-1 self-stretch text-green-500 font-semibold line-clamp-2 leading-6 sm:leading-7"
              as="span"
            >
              {entityName}
            </Typography>
          </div>

          {(hasDays || canNavigateDays) && (
            <ScheduledDaysHeader
              days={days}
              prevDisabled={offset === 0}
              nextDisabled={!hasMore}
              onPrev={goPrev}
              onNext={goNext}
            />
          )}
        </div>
      </div>

      <div
        className="flex flex-col gap-5 px-6 pb-5 w-full overflow-y-auto min-h-0"
        onScroll={handleScroll}
      >
        {isLoading && <SlotsSkeleton variant="modal" columns={limitDays} />}

        {!isLoading && days.length === 0 && (
          <Typography variant={TypographyVariant.BODY_TEXT} className="text-center text-gray-500 py-6">
            Nenhum horário disponível.
          </Typography>
        )}

        {hasDays && (
          <>
            <div className="flex items-start justify-center w-full">
              <div className="w-8 shrink-0" />
              <div className="flex-1 flex gap-4 items-start min-w-0">
                {days.map((day) => (
                  <ScheduledDayColumn
                    key={day.date}
                    slots={day.slots}
                    visibleSlotsLimit={visibleSlotsLimit}
                    slotClassName="py-2 text-base"
                    onSelectSlot={onSelectSlot}
                  />
                ))}
              </div>
              <div className="w-8 shrink-0" />
            </div>
            {(canExpand || isExpanded) && (
              <Button
                variant="text"
                color="primary"
                startIcon={isExpanded ? faMinus : faPlus}
                startIconSize="xs"
                startIconClassName="text-xs text-green-500"
                className="self-center w-auto px-4 py-2 gap-2 shrink-0"
                data-testid={DataTestIdEnum.SEARCH_CARD_CLINIC_SCHEDULED_TOGGLE_EXPAND}
                onClick={toggleExpand}
              >
                {isExpanded ? 'Mostrar menos' : 'Ver outros horários'}
              </Button>
            )}
          </>
        )}
      </div>
    </>
  );
}
